import { createSlice } from "@reduxjs/toolkit";
import { fetchIssuesData } from "../Thunks/issuesThunk";
import {
  fetchAllWorkItems,
  fetchAllWorkItemsForGlobalKanban,
  fetchMyWorkItems,
  fetchWorkItemsByProjectId,
} from "../Thunks/projectManagementThunk";
import { addAsyncResourceThunk, isSupersededRequestPayload } from "../helpers/asyncResource";
import {
  isEpicChildWorkItemsFetchArg,
  normalizeEpicParentIdFromFetchArg,
} from "../Thunks/projectManagementThunkUtils";

const initialEpicChildWorkItems = {
  loading: false,
  requestId: null,
  projectId: null,
  parentId: null,
};

const initialState = {
  data: null,
  loading: false,
  error: null,
  /** Latest `fetchWorkItemsByProjectId` RTK `requestId` — ignore stale fulfilled/rejected when requests overlap (Kanban vs paginated Work Items). */
  projectWorkItemsRequestId: null,
  /** GET /work-items/:projectId?parentIds=… with skipIssuesSlice (epic detail children). */
  epicChildWorkItems: initialEpicChildWorkItems,
};

export const issuesSlice = createSlice({
  name: "issues",
  initialState,
  reducers: {
    clearIssues: (state) => {
      state.data = null;
      state.error = null;
      state.projectWorkItemsRequestId = null;
      state.epicChildWorkItems = initialEpicChildWorkItems;
    },
    /**
     * Entering project Work Items: clear stale list + `meta` (Kanban uses different page/limit) and
     * keep `loading` true until the list GET for this tab completes.
     */
    prepareProjectWorkItemsTab: (state) => {
      state.loading = true;
      state.error = null;
      state.data = null;
      state.projectWorkItemsRequestId = null;
    },
    updateIssueStatus: (state, action) => {
      const { issueId, newStatus, sprintId } = action.payload;
      if (state.data?.list) {
        const issue = state.data.list.find((i) => i.id === issueId);
        if (issue) {
          issue.status = newStatus;
          issue.updatedAt = new Date().toISOString();
          if (sprintId !== undefined) {
            issue.sprintId = sprintId;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    addAsyncResourceThunk(builder, fetchIssuesData);
    // Project list fetch — optional `skipIssuesSlice` (e.g. IssueDetail epic dropdown) must not
    // toggle `loading` or replace list data (avoids full-page loader behind work-item modal).
    builder
      .addCase(fetchWorkItemsByProjectId.pending, (state, action) => {
        const arg = action.meta?.arg;
        if (isEpicChildWorkItemsFetchArg(arg)) {
          state.epicChildWorkItems = {
            loading: true,
            requestId: action.meta.requestId,
            projectId: String(arg.projectId),
            parentId: normalizeEpicParentIdFromFetchArg(arg.body.parentIds),
          };
          return;
        }
        if (arg?.skipIssuesSlice) return;
        state.projectWorkItemsRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkItemsByProjectId.fulfilled, (state, action) => {
        const arg = action.meta?.arg;
        if (isEpicChildWorkItemsFetchArg(arg)) {
          if (action.meta.requestId !== state.epicChildWorkItems.requestId) return;
          state.epicChildWorkItems.loading = false;
          return;
        }
        if (arg?.skipIssuesSlice) return;
        if (action.meta.requestId !== state.projectWorkItemsRequestId) return;
        state.loading = false;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(fetchWorkItemsByProjectId.rejected, (state, action) => {
        const arg = action.meta?.arg;
        if (isEpicChildWorkItemsFetchArg(arg)) {
          if (isSupersededRequestPayload(action.payload)) return;
          if (action.meta.requestId !== state.epicChildWorkItems.requestId) return;
          state.epicChildWorkItems.loading = false;
          return;
        }
        if (arg?.skipIssuesSlice) return;
        if (isSupersededRequestPayload(action.payload)) return;
        if (action.meta.requestId !== state.projectWorkItemsRequestId) return;
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Something went wrong";
      });
    addAsyncResourceThunk(builder, fetchAllWorkItems, {
      onPending: (state) => {
        state.data = null;
      },
      onFulfilled: (state, action) => {
        state.data = action.payload;
      },
    });
    addAsyncResourceThunk(builder, fetchAllWorkItemsForGlobalKanban, {
      onPending: (state) => {
        state.data = null;
      },
      onFulfilled: (state, action) => {
        state.data = action.payload;
      },
    });
    builder
      .addCase(fetchMyWorkItems.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Avoid one paint of stale rows (e.g. global "all" list) before this request's fulfilled.
        state.data = null;
      })
      .addCase(fetchMyWorkItems.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(fetchMyWorkItems.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Something went wrong";
      });
  },
});

export const { clearIssues, prepareProjectWorkItemsTab, updateIssueStatus } = issuesSlice.actions;
export const selectIssuesData = (state) => state.issues.data;
export const selectIssuesLoading = (state) => state.issues.loading;
export const selectIssuesError = (state) => state.issues.error;

/** True while GET /work-items/:projectId?parentIds=… for this epic is in flight. */
export const selectEpicChildWorkItemsLoading = (state, projectId, parentId) => {
  const slot = state.issues?.epicChildWorkItems;
  if (!slot?.loading) return false;
  return String(slot.projectId) === String(projectId) && String(slot.parentId) === String(parentId);
};

export default issuesSlice.reducer;
